import { ProductCategory } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { CategoryDrawer, CategorySelectorItem, SearchFilterButton } from '@components';
import { useAppDispatch } from '@hooks';
import { useMediaQuery, useTheme } from '@mui/material';
import { setModalState } from '@store/modal/modal.slice';
import { t } from '@translations';
import { useMemo } from 'react';

// STYLES
//--------

// TYPES
//-------

export type CategorySelectorProps = {
  skipEmptyCategories?: boolean;
  isCategorySelected: (category: ProductCategory) => boolean;
  categories?: ProductCategory[];
  totalCount?: number;
  showFiltersAndPagination?: boolean;
  handleCategoryChange: (category: ProductCategory) => void;
  isCategoryOpened: (category: ProductCategory) => boolean;
};

// COMPONENTS
//------------
export const CategorySelector = ({
  skipEmptyCategories,
  isCategorySelected,
  categories = [],
  totalCount,
  showFiltersAndPagination,
  handleCategoryChange,
  isCategoryOpened,
}: CategorySelectorProps) => {
  // Screen size & drawer
  const modalId = 'category-selector';
  const theme = useTheme();
  const isBreakpointMedium = useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useAppDispatch();

  const openMobileModal = () => dispatch(setModalState({ id: modalId, isVisible: true }));

  // Add 'All categories' link
  const linkToAllCategories: ProductCategory = useMemo(
    () => ({
      categoryLink: '',
      displayName: t(TextCodes.Search.AllCategories),
      productCount: totalCount,
    }),
    [totalCount]
  );

  const mappedCategories = useMemo(
    () => (skipEmptyCategories ? [linkToAllCategories, ...categories] : categories),
    [categories, linkToAllCategories, skipEmptyCategories]
  );

  // Main content
  const content = (
    <div>
      {mappedCategories?.map((category, key) => (
        <CategorySelectorItem
          onChange={handleCategoryChange}
          isCategorySelected={isCategorySelected}
          category={category}
          key={key}
          skipEmptyCategories={skipEmptyCategories}
          isCategoryOpened={isCategoryOpened}
        />
      ))}
    </div>
  );

  // Selected category & mobile title
  const flatCategoryList = useMemo(() => {
    let categories = [...mappedCategories];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (let [_, category] of Object.entries(mappedCategories)) {
      if (category?.nestedCategories) {
        categories = categories.concat(category?.nestedCategories);
      }
    }
    return categories;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCategorySelected]);
  const selectedCategory = flatCategoryList.find((x) => isCategorySelected(x));

  // Return either drawer on mobile or content on desktop
  return isBreakpointMedium ? (
    content
  ) : (
    <>
      <SearchFilterButton onClick={openMobileModal} title={selectedCategory?.displayName} />
      <CategoryDrawer
        modalId={modalId}
        categories={mappedCategories}
        showFiltersAndPagination={showFiltersAndPagination}
        handleCategoryChange={handleCategoryChange}
        isCategorySelected={isCategorySelected}
        skipEmptyCategories={skipEmptyCategories}
        isCategoryOpened={isCategoryOpened}
      />
    </>
  );
};
